import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BarChart3, ClipboardList, PenTool, TrendingUp, CheckCircle, Wallet } from 'lucide-react';
import {
  reportsApi,
  TeamPerformanceReportDto,
  ReportFilterDto,
  formatCurrency,
  formatPercentage,
  handleApiError
} from '../../services/reportsService';

const ReportContainer = styled.div`
  padding: 0;
`;

const SummarySection = styled.div`
  margin-bottom: 28px;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SummaryIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const SummaryTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryItem = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  padding-top: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const SummaryItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const SummaryItemIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => `${props.$color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  color: var(--error-color);
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  font-size: 14px;
`;

interface TeamPerformanceReportProps {
  filter: ReportFilterDto;
}

const TeamPerformanceReport: React.FC<TeamPerformanceReportProps> = ({ filter }) => {
  const [report, setReport] = useState<TeamPerformanceReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsApi.getTeamPerformanceReport(filter);
      setReport(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingContainer>טוען דוח ביצועי צוות...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!report) {
    return null;
  }

  const summary = report.monthlySummary;

  return (
    <ReportContainer>
      {/* Monthly Summary */}
      <SummarySection>
        <SummaryHeader>
          <SummaryIcon>
            <BarChart3 size={20} />
          </SummaryIcon>
          <SummaryTitle>סיכום חודשי</SummaryTitle>
        </SummaryHeader>
        <SummaryGrid>
          <SummaryItem $accentColor="var(--primary-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--primary-color)">
                <ClipboardList size={20} />
              </SummaryItemIcon>
              <SummaryLabel>לידים חדשים</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{summary.totalNewLeads}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="var(--success-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--success-color)">
                <PenTool size={20} />
              </SummaryItemIcon>
              <SummaryLabel>חתימות</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{summary.totalSignatures}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="#6366f1">
            <SummaryItemHeader>
              <SummaryItemIcon $color="#6366f1">
                <TrendingUp size={20} />
              </SummaryItemIcon>
              <SummaryLabel>אחוז המרה</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{formatPercentage(summary.conversionPercentage)}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="var(--info-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--info-color)">
                <CheckCircle size={20} />
              </SummaryItemIcon>
              <SummaryLabel>תיקים מאושרים</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue>{summary.totalApprovedCases}</SummaryValue>
          </SummaryItem>
          <SummaryItem $accentColor="var(--warning-color)">
            <SummaryItemHeader>
              <SummaryItemIcon $color="var(--warning-color)">
                <Wallet size={20} />
              </SummaryItemIcon>
              <SummaryLabel>סכום גמלאות</SummaryLabel>
            </SummaryItemHeader>
            <SummaryValue style={{ fontSize: '22px' }}>
              {formatCurrency(summary.totalBenefitAmount)}
            </SummaryValue>
          </SummaryItem>
        </SummaryGrid>
      </SummarySection>
    </ReportContainer>
  );
};

export default TeamPerformanceReport;
